import { useEffect, useState } from "react";
import { Loader2, ImagePlus, X, ArrowUp, ArrowDown, Video, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const inputCls = "w-full border border-navy/20 bg-white px-4 py-3 text-sm text-navy focus:outline-none focus:border-rouge transition-colors duration-300";
const labelCls = "block font-heading uppercase tracking-wider text-xs text-navy/70 mb-1.5";

const mediaUrl = (path) => `${API_BASE}/api/files/${path}`;

function PhotoField({ value, onChange, testId }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd);
      onChange(data.path);
      toast.success("Photo téléversée");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      {value ? (
        <div className="flex items-center gap-4" data-testid={`${testId}-preview`}>
          <img src={mediaUrl(value)} alt="Aperçu" className="h-24 w-40 object-cover border border-navy/10" />
          <button type="button" onClick={() => onChange("")} data-testid={`${testId}-remove`} className="inline-flex items-center gap-2 border border-navy/25 text-navy text-xs font-heading uppercase tracking-wider px-4 py-2.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
            <X size={14} /> Supprimer la photo
          </button>
        </div>
      ) : (
        <label data-testid={`${testId}-upload`} className="flex items-center justify-center gap-3 border border-dashed border-navy/25 px-6 py-8 cursor-pointer text-steel text-sm hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
          {uploading ? "Téléversement…" : "Ajouter une photo (jpg, png, webp)"}
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" disabled={uploading} onChange={(e) => upload(e.target.files?.[0])} />
        </label>
      )}
    </div>
  );
}

function VideoField({ form, setForm, testId }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-video", fd);
      setForm({ ...form, video_type: "file", video_path: data.path, video_url: "" });
      toast.success("Vidéo téléversée");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="space-y-3">
      <select
        data-testid={`${testId}-type`}
        value={form.video_type}
        onChange={(e) => setForm({ ...form, video_type: e.target.value })}
        className={inputCls}
      >
        <option value="">Aucune vidéo</option>
        <option value="file">Fichier vidéo (mp4, webm, mov)</option>
        <option value="youtube">Lien YouTube</option>
        <option value="vimeo">Lien Vimeo</option>
      </select>
      {form.video_type === "file" && (
        form.video_path ? (
          <div className="flex items-center gap-4" data-testid={`${testId}-file-preview`}>
            <span className="inline-flex items-center gap-2 text-sm text-navy bg-mist px-4 py-2.5"><Video size={15} className="text-rouge" /> Vidéo téléversée</span>
            <button type="button" onClick={() => setForm({ ...form, video_path: "", video_type: "" })} data-testid={`${testId}-file-remove`} className="text-xs text-steel hover:text-rouge underline transition-colors">Supprimer</button>
          </div>
        ) : (
          <label data-testid={`${testId}-file-upload`} className="flex items-center justify-center gap-3 border border-dashed border-navy/25 px-6 py-8 cursor-pointer text-steel text-sm hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Video size={16} />}
            {uploading ? "Téléversement…" : "Choisir une vidéo (100 Mo max)"}
            <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" disabled={uploading} onChange={(e) => upload(e.target.files?.[0])} />
          </label>
        )
      )}
      {(form.video_type === "youtube" || form.video_type === "vimeo") && (
        <input
          data-testid={`${testId}-url`}
          type="url"
          value={form.video_url}
          onChange={(e) => setForm({ ...form, video_url: e.target.value })}
          className={inputCls}
          placeholder={form.video_type === "youtube" ? "https://www.youtube.com/watch?v=…" : "https://vimeo.com/…"}
        />
      )}
    </div>
  );
}

function AxeEditor({ axe, onSaved }) {
  const [form, setForm] = useState(axe);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({ numero: "", sous_titre: "", galerie: [], mini_blocs: [], bouton_label: "", bouton_lien: "", video_type: "", video_path: "", video_url: "", photo: "", texte: "", titre: "", ...axe });
  }, [axe]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setGallery = (galerie) => setForm({ ...form, galerie });
  const move = (i, dir) => {
    const g = [...form.galerie];
    const j = i + dir;
    if (j < 0 || j >= g.length) return;
    [g[i], g[j]] = [g[j], g[i]];
    setGallery(g);
  };
  const setBloc = (i, k, v) => setForm({ ...form, mini_blocs: form.mini_blocs.map((b, j) => (j === i ? { ...b, [k]: v } : b)) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/projet/${axe.axe}`, form);
      toast.success(`Axe ${axe.numero} enregistré`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-white border border-navy/10 p-6 sm:p-8 space-y-5" data-testid={`projet-editor-${axe.axe}`}>
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="sm:col-span-2"><label className={labelCls}>Titre</label><input data-testid={`projet-field-titre-${axe.axe}`} required value={form.titre} onChange={set("titre")} className={inputCls} /></div>
        <div><label className={labelCls}>Numéro / thème</label><input data-testid={`projet-field-numero-${axe.axe}`} value={form.numero} onChange={set("numero")} className={inputCls} placeholder="01 — Performance" /></div>
        <div><label className={labelCls}>Sous-titre (facultatif)</label><input data-testid={`projet-field-soustitre-${axe.axe}`} value={form.sous_titre} onChange={set("sous_titre")} className={inputCls} /></div>
        <div className="sm:col-span-2"><label className={labelCls}>Texte</label><textarea data-testid={`projet-field-texte-${axe.axe}`} rows={8} value={form.texte} onChange={set("texte")} className={inputCls} /></div>
        <div className="sm:col-span-2"><span className={labelCls}>Photo principale</span><PhotoField value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} testId={`projet-photo-${axe.axe}`} /></div>

        <div className="sm:col-span-2">
          <span className={labelCls}>Galerie (photos secondaires)</span>
          {form.galerie.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-3">
              {form.galerie.map((p, i) => (
                <div key={p + i} className="relative group" data-testid={`projet-gal-${axe.axe}-${i}`}>
                  <img src={mediaUrl(p)} alt="" className="w-full aspect-[4/3] object-cover border border-navy/10" />
                  <div className="absolute inset-0 bg-navy/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    <button type="button" onClick={() => move(i, -1)} className="p-1.5 text-white hover:text-rouge" aria-label="Monter"><ArrowUp size={14} /></button>
                    <button type="button" onClick={() => move(i, 1)} className="p-1.5 text-white hover:text-rouge" aria-label="Descendre"><ArrowDown size={14} /></button>
                    <button type="button" onClick={() => setGallery(form.galerie.filter((_, j) => j !== i))} data-testid={`projet-gal-del-${axe.axe}-${i}`} className="p-1.5 text-white hover:text-rouge" aria-label="Supprimer"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <PhotoField value="" onChange={(v) => v && setGallery([...form.galerie, v])} testId={`projet-gal-add-${axe.axe}`} />
        </div>

        <div className="sm:col-span-2"><span className={labelCls}>Vidéo (facultative)</span><VideoField form={form} setForm={setForm} testId={`projet-video-field-${axe.axe}`} /></div>

        <div><label className={labelCls}>Libellé du bouton</label><input data-testid={`projet-field-btn-label-${axe.axe}`} value={form.bouton_label} onChange={set("bouton_label")} className={inputCls} /></div>
        <div><label className={labelCls}>Lien du bouton</label><input data-testid={`projet-field-btn-lien-${axe.axe}`} value={form.bouton_lien} onChange={set("bouton_lien")} className={inputCls} placeholder="/equipes" /></div>
      </div>

      {axe.axe === 3 && (
        <div>
          <span className={labelCls}>Mini-blocs visuels</span>
          <div className="space-y-4">
            {form.mini_blocs.map((b, i) => (
              <div key={i} className="bg-mist p-4 grid sm:grid-cols-2 gap-4" data-testid={`projet-bloc-${axe.axe}-${i}`}>
                <div><label className="block text-[10px] uppercase tracking-wider text-steel mb-1">Titre du bloc</label><input value={b.titre} onChange={(e) => setBloc(i, "titre", e.target.value)} className={inputCls} /></div>
                <div><label className="block text-[10px] uppercase tracking-wider text-steel mb-1">Lien (facultatif)</label><input value={b.lien} onChange={(e) => setBloc(i, "lien", e.target.value)} className={inputCls} placeholder="/projets/senegal" /></div>
                <div className="sm:col-span-2"><span className="block text-[10px] uppercase tracking-wider text-steel mb-1">Photo du bloc</span><PhotoField value={b.photo} onChange={(v) => setBloc(i, "photo", v)} testId={`projet-bloc-photo-${axe.axe}-${i}`} /></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <button type="submit" disabled={saving} data-testid={`projet-save-${axe.axe}`} className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-7 py-3.5 hover:bg-navy disabled:opacity-60 transition-colors duration-300">
        {saving && <Loader2 size={15} className="animate-spin" />}
        Enregistrer l'axe {axe.numero}
      </button>
    </form>
  );
}

export default function AdminProjet() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(1);

  const load = () => api.get("/projet").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  if (!data) {
    return <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10"><div className="flex justify-center py-20"><Loader2 className="animate-spin text-rouge" size={28} /></div></main>;
  }

  const current = tab === 0 ? data.conclusion : data.axes.find((a) => a.axe === tab);

  return (
    <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10">
      <div className="flex flex-wrap gap-2 mb-8" data-testid="projet-admin-tabs">
        {[1, 2, 3, 4].map((n) => (
          <button key={n} onClick={() => setTab(n)} data-testid={`projet-admin-tab-${n}`} className={`font-heading uppercase tracking-wider text-xs px-5 py-2.5 border transition-[background-color,color,border-color] duration-300 ${tab === n ? "bg-navy border-navy text-white" : "border-navy/20 text-navy/70 hover:border-rouge hover:text-rouge"}`}>
            Axe {String(n).padStart(2, "0")}
          </button>
        ))}
        <button onClick={() => setTab(0)} data-testid="projet-admin-tab-conclusion" className={`font-heading uppercase tracking-wider text-xs px-5 py-2.5 border transition-[background-color,color,border-color] duration-300 ${tab === 0 ? "bg-navy border-navy text-white" : "border-navy/20 text-navy/70 hover:border-rouge hover:text-rouge"}`}>
          Conclusion
        </button>
      </div>
      {current && <AxeEditor key={current.axe} axe={current} onSaved={load} />}
    </main>
  );
}
